import { css, SerializedStyles } from '@emotion/react';
import {
    CheckboxSize,
    createResponsiveStyles,
    objectToCssVariables,
    ResponsiveStyleValue,
    useHoneycombTheme,
} from '../../theming';

// Static styles
export const CHECKBOX_STATIC_STYLES: SerializedStyles = css`
    --checkbox-size-xs: 20px;
    --checkbox-size-s: 24px;
    --checkbox-size-m: 32px;

    --checkbox-size: var(--checkbox-size-m);
    --checkbox-icon-size: calc(var(--checkbox-size) * 0.75);

    --checkbox-border-width: 2px;
    --checkbox-radius: 4px;

    --checkbox-color-default: var(--hc-color-grey-base);
    --checkbox-color-checked: var(--hc-color-primary-base);

    &[data-error] {
        --checkbox-color-default: var(--hc-color-system-error-base);
        --checkbox-color-checked: var(--hc-color-system-error-base);
    }

    position: relative;
    display: inline-flex;
    flex-shrink: 0;
    width: var(--checkbox-size);
    height: var(--checkbox-size);

    [data-input]:not(:disabled) {
        cursor: pointer;
    }

    [data-input],
    [data-indicator] {
        appearance: none;
        margin: 0;
        outline: none;

        -webkit-tap-highlight-color: transparent;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;

        border-radius: var(--checkbox-radius);
        background-color: var(--hc-color-common-white);
        border-style: solid;
        border-width: var(--checkbox-border-width);
        border-color: var(--checkbox-color-default);

        &:checked,
        &[data-checked] {
            background-color: var(--checkbox-color-checked);
            border-color: transparent;
        }

        &:checked + svg,
        &[data-checked] + svg {
            opacity: 1;
            transform: scale3d(1, 1, 1);
        }

        &:disabled,
        &[data-disabled],
        &:disabled + svg,
        &[data-disabled] + svg {
            opacity: var(--hc-disabled-opacity);
        }

        &:focus-visible {
            outline-offset: 0;
            outline: 2px solid var(--hc-color-action-focus);
        }

        &:hover:not(:disabled):not([data-disabled]) {
            box-shadow: var(--hc-hover-shadow);
        }
    }

    svg {
        pointer-events: none;
        color: var(--hc-color-common-white);

        transform: scale3d(0, 0, 1);
        opacity: 0;
        transition: opacity 100ms ease, transform 200ms ease;

        width: var(--checkbox-icon-size);
        height: var(--checkbox-icon-size);

        position: absolute;
        top: calc(50% - var(--checkbox-icon-size) / 2);
        left: calc(50% - var(--checkbox-icon-size) / 2);
    }
`;

export function useCheckboxCssVariables(size: ResponsiveStyleValue<CheckboxSize>): React.CSSProperties {
    const { theme } = useHoneycombTheme();
    const componentTheme = theme.components.themes.Checkbox;

    return {
        ...objectToCssVariables(theme.fn.resolveFlatColorObject(componentTheme.colors), 'checkbox-color-'),
        ...createResponsiveStyles(
            [
                {
                    value: size,
                    getStyles: (value: CheckboxSize) => {
                        return {
                            '--checkbox-size': `var(--checkbox-size-${value})`,
                        };
                    },
                },
            ],
            theme
        ),
    } as React.CSSProperties;
}
